import React from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import CustomButton from 'components/CustomButton';
import useCodingStore from './CodingStore';
import ConfirmationDialog from 'dialogs/ConfirmationDialog';
import { AssessmentStates, QuestionStates, QuestionsTypes, SectionWiseData } from './constant';
import Timer from './Timer';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    height: '76px',
    padding: theme.spacing(4,10),
    borderBottom: `1px solid ${theme.palette.neutral['clr-200']}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'fixed',
    top: 0,
  },
  name: {
    display: 'flex',
    width: '33.33%',
  },
  action: {
    display: 'flex',
    width: '33.33%',
    justifyContent: 'flex-end',
    position : 'relative'
  },
}));

const Header = ({ }) => {
  const classes = useStyles();
  
  const {title} = useCodingStore(state => state.challenge);
  const [
    hints, analysis, questionType, questionState, assessmentState,
    setAssessmentState, setSectionTimeData
  ] = useCodingStore(state => [
    state.hints, state.analysis, state.questionType, state.questionState, state.assessmentState,
    state.setAssessmentState, state.setSectionTimeData
  ]);

  const [openEndSessionDialog, setOpenEndSessionDialog] = React.useState(false);
  
  const { totalTime=0, warningTime=0, showTimer=false } = React.useMemo(() => {
    if ([
      QuestionStates.INTERVIEWER_ASKING,
      QuestionStates.FETCHING_QUESTIONS,
    ].includes(questionState) || assessmentState !== AssessmentStates.RUNNING) {
      return { totalTime: 0, warningTime: 0 };
    }
  
    const isCoding = [QuestionStates.CODING,QuestionStates.CODE_EVALUATING].includes(questionState);
    const timeKey = isCoding ? 'codingTime' : 'explanationTime';
    const warningKey = isCoding ? 'codingWarningTime' : 'explanationWarningTime';
  
    return {
      totalTime: SectionWiseData[questionType]?.[timeKey] ?? 0,
      warningTime: SectionWiseData[questionType]?.[warningKey] ?? 0,
      showTimer: true
    };
  }, [questionState, questionType]);

  React.useEffect(()=>{
    setSectionTimeData({
      totalTime,
      warningTime,
      showTimer
    });
  }, [totalTime, warningTime, showTimer]);

  const {
    endInterviewTitle= "Finish Interview",
    endInterviewMessage= "Awesome job! Would you like to submit your interview now?",
    endingSoon=false
  } = React.useMemo(() => {

    if(!hints.length && Object.keys(analysis[QuestionsTypes.MAIN].analysis).length === 0){
      return {
        endInterviewTitle: "Hold on!",
        endInterviewMessage: "Interviewer hasn’t completed yet! Please finish it to receive your feedback.",
        endingSoon: true
      }
    }else if(analysis[QuestionsTypes.FOLLOW_UP_2]?.submitted){
      return {
        endInterviewTitle: "Finish Interview",
        endInterviewMessage: "Awesome job! Would you like to submit your interview now?",
        endingSoon: false
      }
    }else{
      return {
        endInterviewTitle: "Ending so soon?",
        endInterviewMessage: "You haven’t started the challenge yet! Ending now means losing a credit and missing out on valuable feedback report. Ready to give it a go?",
        endingSoon: true
      }
    }

  }, [hints, analysis]);

  const handleEndSession = () => {
    setAssessmentState(AssessmentStates.FINISHING);
    setOpenEndSessionDialog(false);
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.name}>
        <Typography variant="h5-medium">
          {title}
        </Typography>
      </Box>

      {
        showTimer &&
          <Box style={{ opacity: 0 }}>
            <Timer
              totalTime={totalTime}
              warningTime={warningTime}
            />
          </Box>
      }
      
      <Box className={classes.action}>
        <CustomButton
          variant='outlined'
          size='medium'
          startIcon={<CloseIcon />}       
          onClick={() => {setOpenEndSessionDialog(true)}}
        >
          <Box  id='end-session' 
          sx={{
            position : 'absolute' , top : '20px' , left : 0 , width : '20px' , height : '20px'
          }}
          >
            <Box id='end-session-container'></Box>
          </Box>
          <Box >End Session</Box>
        </CustomButton>
      </Box>

      <ConfirmationDialog
        open={openEndSessionDialog}
        title={<span style={{ color: endingSoon ? "#E2483C" : "#1F2229"}}>{endInterviewTitle}</span>}
        message={endInterviewMessage}
        onClose={() => setOpenEndSessionDialog(false)}
        primaryAction={endingSoon ? 'Resume challenge' : "Yes"}
        secondaryAction={endingSoon ? 'End anyway' : "No"}
        onPrimaryAction={() => endingSoon ? setOpenEndSessionDialog(false) : handleEndSession()}
        onSecondaryAction={()=> endingSoon ? handleEndSession() : setOpenEndSessionDialog(false)}
      />
    </Box>
  );
};

export default Header;